import { observer } from 'mobx-react'
import PageWrapper from 'pages/Components/PageWrapper'
import React, { Key, useEffect, useRef, useState } from 'react'
import CustomButton from 'base/ui/components/Button';
import { Filter } from 'base/ui/components/Icons';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { HiSearch, HiOutlineExternalLink } from 'react-icons/hi';
import { Form, Input, Select, Table, Tooltip, message, Radio, Modal, Upload } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { ActionType, ProColumns } from '@ant-design/pro-components';
import { Link, useNavigate } from 'react-router-dom';
import { wasteProductPresenter, wasteProductStore, wasteGroupingEnvironmentPermitPresenter, wasteGroupingEnvironmentPermitStore, commonMasterDataPresenter, commonMasterDataStore, userStore } from 'stores/root_store';
import CustomButtonIcon from 'base/ui/components/Button/buttonIcon';
import { BASE_API_URL, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants';
import { WasteProductModel } from 'services/RawData/wasteProduct_services';
import { SearchModel } from 'pages/model';
import { PencilEdit } from 'base/ui/components/Icons';
import { usePermission } from 'hooks/usePermission';
import { checkPermissionWithId } from 'utils/permissionHelper';
import { getAccessToken, getRoles } from 'utils/localStorageHelper';
import * as Icon from 'base/ui/components/Icons';
import { useTranslation } from 'react-i18next';
import { dropdownStatus } from 'utils/dropdown';
import { customFilterOption } from 'utils/searchHelper';
import SearchMultiplePopup from 'pages/synchPO/SearchMultiplePopup';
import { ExportButton } from 'pages/Components/FunctionButton';
import { cloneDeep, omit } from 'lodash';

const initSearch = {
  paging: {
    pageIndex: 1,
    pageSize: PAGE_SIZE_DEFAULT,
  },
  actived: true,
}

// thông tin chất thải/ phế liệu
const createWasteProduct = () => {
  return observer(() => {
    const { t } = useTranslation();
    document.title = t('masterdata.waste-product.title') + ' - ' + SYSTEM_TITLE;
    usePermission('INDEX');
    const navigate = useNavigate();

    const isCreate = checkPermissionWithId('CREATE')
    const isEdit = checkPermissionWithId('EDIT')
    const isDelete = checkPermissionWithId('DELETE')

    // Check role SYSADMIN từ localStorage
    const roles = getRoles() || [];
    const rolesArray = Array.isArray(roles) ? roles : (typeof roles === 'string' ? roles.split(',').map(r => r.trim()) : []);
    const isSysAdmin = rolesArray.includes('SYSADMIN');

    const [form] = Form.useForm()
    const [openDrawer, setOpenDrawer] = useState(false);
    const [dataSearch, setDataSearch] = useState<SearchModel>(initSearch);
    const [dataTable, setDataTable] = useState<WasteProductModel[]>([])
    const [tableLoading, setTableLoading] = useState(false);
    const [showModalProductCode, setShowModalProductCode] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [importLoading, setImportLoading] = useState(false);

    const [wasteGroupOptions, setWasteGroupOptions] = useState<{ label: string; value: string; code?: string }[]>([]);
    const [wasteGroupingContractOptions, setWasteGroupingContractOptions] = useState<{ label: string; value: string }[]>([]);
    const [wasteTypeOptions, setWasteTypeOptions] = useState<{ label: string; value: string }[]>([]);

    const showDrawer = () => {
      form.setFieldsValue({
        productCode: dataSearch.productCode,
        productName: dataSearch.productName,
        wasteGroupingEnvironmentPermitId: dataSearch.wasteGroupingEnvironmentPermitId,
        wasteGroupingEnvironmentPermitIds: dataSearch.wasteGroupingEnvironmentPermitIds,
        wasteGroupingContractIds: dataSearch.wasteGroupingContractIds,
        wasteGroup: dataSearch.wasteGroup,
        actived: dataSearch.actived,
      });
      setOpenDrawer(true);
    };

    const onCloseDrawer = () => {
      setOpenDrawer(false);
    };
    const tableRef = useRef<ActionType>();

    const [isLoading, setIsLoading] = useState(false)

    const defaultColumn: ProColumns<WasteProductModel>[] = [
      {
        title: t('masterdata.product.stt'),
        dataIndex: 'stt',
        key: 'stt',
        width: 50,
        align: 'center',
        render: (text) => <div className="!text-center">{text}</div>,
        fixed: 'left',
      },
      {
        title: t('masterdata.waste-product.product-code'),
        dataIndex: 'productCode',
        key: 'productCode',
        width: 150,
        className: 'min-w-[150px]',
        fixed: 'left',
        render: (_, record) => {
          return isEdit ? (
            <Link to={`/MasterData/WasteProduct/${record.wasteProductId}`}>{record.productCode}</Link>
          ) : (
            <div>{record.productCode}</div>
          );
        },
      },
      {
        title: t('masterdata.waste-product.product-name'),
        dataIndex: 'productName',
        key: 'productName',
        width: 200,
        className: 'min-w-[200px]',
        fixed: 'left',
      },
      {
        title: t('masterdata.waste-product.waste-group'),
        dataIndex: 'wasteGroup',
        key: 'wasteGroup',
        width: 150,
        className: 'min-w-[150px]',
        render: (_, record) => record.wasteGroup || '-',
      },

      {
        title: t('masterdata.waste-product.waste-subgroup-gpmt-code'),
        dataIndex: 'wasteGroupingEnvironmentPermitCustomCode',
        key: 'wasteGroupingEnvironmentPermitCustomCode',
        width: 200,
        className: 'min-w-[200px]',
        render: (_, record) => record.wasteGroupingEnvironmentPermitCustomCode || '-',
      },
      {
        title: t('masterdata.waste-product.waste-subgroup-gpmt-name'),
        dataIndex: 'wasteGroupingEnvironmentPermitName',
        key: 'wasteGroupingEnvironmentPermitName',
        width: 250,
        className: 'min-w-[250px]',
        render: (_, record) => record.wasteGroupingEnvironmentPermitName || '-',
      },

      {
        title: t('masterdata.waste-product.waste-subgroup-contract'),
        dataIndex: 'wasteGroupingContractName',
        key: 'wasteGroupingContractName',
        width: 200,
        className: 'min-w-[200px]',
        render: (_, record) => record.wasteGroupingContractName || '-',
      },
      {
        title: t('masterdata.product.status'),
        dataIndex: 'actived',
        key: 'actived',
        width: 100,
        align: 'center',
        render: (_, record) => (
          <div className="flex items-center justify-center">
            {record.actived ? (
              <Icon.CheckStatus className="!fill-[#389e0d]" width="15" />
            ) : (
              <Icon.CloseStatus className="!fill-[#D93133E5]" width="15" />
            )}
          </div>
        ),
      },
      {
        title: '',
        dataIndex: 'functions',
        key: 'functions',
        width: 90,
        align: 'center',
        fixed: 'right',
        hideInTable: isEdit ? false : true,
        hideInSetting: isEdit ? false : true,
        render: (_, record) => {
          return (
            <div className="w-full flex items-center justify-center gap-x-1">
              <Tooltip title={t('btn.edit')}>
                <Link to={record.wasteProductId || ''}>
                  <CustomButtonIcon color="#007BF1">
                    <PencilEdit className="!fill-[#007BF1]" width="14" height="14" />
                  </CustomButtonIcon>
                </Link>
              </Tooltip>
              {isDelete && record.actived && (
                <Tooltip title="Hủy">
                  <CustomButtonIcon
                    color="#FC5C5E"
                    onClick={() => {
                      Modal.confirm({
                        title: 'Xác nhận hủy',
                        icon: <ExclamationCircleFilled />,
                        content: `Bạn có chắc chắn muốn hủy sản phẩm chất thải "${record.productCode}"?`,
                        okText: 'Xác nhận',
                        okType: 'danger',
                        cancelText: 'Hủy',
                        async onOk() {
                          const response = await wasteProductPresenter.updateWasteProductStatus(
                            wasteProductStore,
                            {
                              wasteProductId: record.wasteProductId,
                              wasteProductIds: [record.wasteProductId],
                              actived: false,
                            },
                          );
                          if (response.isSuccess) {
                            message.success(response.message || 'Hủy thành công');
                            tableRef.current?.reload();
                          } else {
                            message.error(response.message || 'Có lỗi xảy ra khi hủy');
                          }
                        },
                      });
                    }}
                  >
                    <Icon.Trash className="!fill-[#FC5C5E]" width="14" height="14" />
                  </CustomButtonIcon>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: t('masterdata.product.creator'),
        dataIndex: 'createByName',
        key: 'createByName',
        width: 150,
        className: 'min-w-[150px]',
      },
      {
        title: t('masterdata.product.created-at'),
        dataIndex: 'createTime',
        key: 'createTime',
        width: 150,
        align: 'center',
        valueType: 'dateTime',
        fieldProps: {
          format: 'DD/MM/YYYY HH:mm:ss',
        },
      },
    ];

    const getDataSearchWasteProduct = async (values: SearchModel) => {
      const request: any = {
        ...values,
      };

      // Map wasteGroupingEnvironmentPermitIds nếu có
      if (request.wasteGroupingEnvironmentPermitIds) {
        const ids = Array.isArray(request.wasteGroupingEnvironmentPermitIds)
          ? request.wasteGroupingEnvironmentPermitIds
          : [request.wasteGroupingEnvironmentPermitIds];
        request.wasteGroupingEnvironmentPermitIds = ids;
      }

      // Map wasteGroupingContractIds nếu có
      if (request.wasteGroupingContractIds) {
        const ids = Array.isArray(request.wasteGroupingContractIds)
          ? request.wasteGroupingContractIds
          : [request.wasteGroupingContractIds];
        request.wasteGroupingContractIds = ids;
      }

      // Map wasteGroup nếu có - API mong đợi string, nếu là array thì lấy giá trị đầu tiên
      // if (request.wasteGroup) {
      // if (Array.isArray(request.wasteGroup)) {
      //   // Nếu là array và có nhiều giá trị, chỉ lấy giá trị đầu tiên
      //   request.wasteGroup = request.wasteGroup[0];
      // }
      // Nếu không phải array thì giữ nguyên (đã là string)
      // }

      setTableLoading(true)
      await wasteProductPresenter.searchWasteProduct(wasteProductStore, request);
      setTableLoading(false)
      if (wasteProductStore.searchWasteProductResponse?.isSuccess) {
        setDataTable(wasteProductStore.searchWasteProductResponse?.data);
        return wasteProductStore.searchWasteProductResponse?.data
      }
      return []
    };

    const loadWasteGroups = async () => {
      await wasteGroupingEnvironmentPermitPresenter.searchWasteGroupingEnvironmentPermits(
        wasteGroupingEnvironmentPermitStore,
        {
          paging: {
            pageIndex: 1,
            pageSize: 99999,
            orderBy: '',
            orderByDesc: '',
          },
          activedOnly: false,
        }
      );
      if (wasteGroupingEnvironmentPermitStore.searchResponse.isSuccess) {
        setWasteGroupOptions(
          (wasteGroupingEnvironmentPermitStore.searchResponse.data || []).map((item) => ({
            label: `${item.wasteGroupingEnvironmentPermitCustomCode ?? ''} | ${item.wasteGroupingEnvironmentPermitName}`,
            value: item.wasteGroupingEnvironmentPermitId || '',
            code: item.wasteGroupingEnvironmentPermitCustomCode || '',
          })),
        );
      }
    };

    const loadWasteGroupingContracts = async () => {
      if (!commonMasterDataStore.wasteGroupingContractOptions.length) {
        await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, false);
      }
      setWasteGroupingContractOptions(
        (commonMasterDataStore.wasteGroupingContractOptions || []).map((item) => ({
          label: item.value,
          value: item.key,
        }))
      );
    };

    const loadWasteTypes = async () => {
      await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'WasteType');
      const options = commonMasterDataStore.commonRespone?.data || [];
      setWasteTypeOptions(
        options.map((item) => ({
          label: item.value || '',
          value: item.key || '',
        }))
      );
    };

    useEffect(() => {
      loadWasteGroups();
      loadWasteGroupingContracts();
      loadWasteTypes();
    }, []);

    return (
      <PageWrapper
        breadcrumb={[{
          text: t('masterdata.waste-product.title')
        }]}
        extras={
          <>
            {isSysAdmin && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setImportLoading(true);
                    try {
                      const result = await wasteProductPresenter.importWasteProductsCreate(file);
                      if (result.isSuccess) {
                        message.success(result.message || t('message.success'));
                        tableRef.current?.reload();
                      } else {
                        message.error(result.message || t('message.error'));
                      }
                    } finally {
                      setImportLoading(false);
                      e.target.value = '';
                    }
                  }}
                />
                <CustomButton
                  onClick={() => fileInputRef.current?.click()}
                  loading={importLoading}
                  disabled={importLoading}
                >
                  {t('btn.import') || 'Import'}
                </CustomButton>
              </>
            )}
            {isSysAdmin && (
              <CustomButton
                onClick={async () => {
                  // dùng điều kiện search hiện tại để export
                  await wasteProductPresenter.exportWasteProducts({
                    ...dataSearch,
                  });
                }}
              >
                {t('btn.export') || 'Export'}
              </CustomButton>
            )}

            {userStore.userPermission?.includes('EXPORT') ? (
              <ExportButton
                title='Export template'
                loading={isLoading} onClick={async () => {
                  setIsLoading(true);
                  const exportRequest = cloneDeep(dataSearch)
                  await wasteProductPresenter.exportTemplateUpdateRequireConfirmPlan(omit(exportRequest, ['paging']))
                  setIsLoading(false);
                }} />
            ) : null}
            {userStore.userPermission?.includes('IMPORT') ? (
              <Upload
                maxCount={1}
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                showUploadList={false}
                onChange={async (info) => {
                  if (info.file.status === "uploading") {
                    setImportLoading(true)
                  }
                  if (info.file.status === 'done') {
                    setImportLoading(false)
                    message.success(info.file.response.message);
                    setDataSearch(initSearch)
                    tableRef.current?.reload()
                  }
                  if (info.file.status === 'error') {
                    message.error(info.file.response.message);
                    setImportLoading(false)
                  }
                }}
                action={BASE_API_URL + '/wasteproduct/import'}
                headers={{
                  Authorization: `Bearer ${getAccessToken()}`,
                }}
                name="File"
              >
                <CustomButton
                  color='#037dd6'
                  type='primary'
                  loading={importLoading}
                  className='!text-white'
                  icon={<Icon.Upload className='!text-white fill-white' color='white' />}>
                  Import
                </CustomButton>
              </Upload>
            ) : null}

            {isCreate && (
              <CustomButton
                type="primary"
                icon={<Icon.Plus className="!fill-white" />}
                onClick={() => navigate('Create')}
              >
                {t('btn.add')}
              </CustomButton>
            )}
            <CustomButton onClick={showDrawer} type="primary" color="#EFF4F9" className="!px-0 w-8">
              <Filter className="fill-primary" />
            </CustomButton>
          </>
        }
      >
        <BoxContainer>
          <CustomProTable<WasteProductModel>
            columns={defaultColumn}
            rowKey="wasteProductId"
            size="small"
            bordered
            loading={tableLoading}
            actionRef={tableRef}
            scroll={{ x: 'auto' }}
            tableAlertRender={false}
            search={false}
            request={async (params = {}, sort, filter) => {
              const data: any = await getDataSearchWasteProduct({
                ...dataSearch,
                paging: {
                  ...dataSearch.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                },
              });

              if (Object.values(sort).length) {
                setDataSearch({
                  ...dataSearch,
                  paging: {
                    ...dataSearch.paging,
                    orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                    orderByDesc: Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : '',
                  },
                });
              }
              return {
                data: data,
                success: wasteProductStore.searchWasteProductResponse?.isSuccess
              };
            }}
            dataSource={dataTable}
            pagination={{
              ...tablePagination(dataSearch, setDataSearch),
              total: wasteProductStore.searchWasteProductResponse?.paging?.recordsTotal
            }}
            options={false}
          />
        </BoxContainer>

        <SearchBox
          className="lg:!w-[50%]"
          footer={[
            <div key="footer" className="flex justify-end">
              <CustomButton
                type="primary"
                icon={<HiSearch />}
                htmlType="submit"
                onClick={() => {
                  form.submit();
                  onCloseDrawer();
                }}
              >
                Tìm kiếm
              </CustomButton>
            </div>,
          ]}
          onClose={onCloseDrawer}
          open={openDrawer}
        >
          <SearchForm
            form={form}
            onFinish={(values) => {
              const search = {
                ...values,
                paging: {
                  ...dataSearch.paging,
                  pageIndex: 1
                }
              }
              setDataSearch(search)
              tableRef.current?.reload()
            }}
            className="gap-x-12"
            layout="vertical"
            column="col-2"
          >
            <button type='submit' hidden></button>
            {/* 1. Nhóm chất thải */}
            <SearchItem label={t('masterdata.waste-product.waste-group')} name="wasteGroup">
              <Select
                options={wasteTypeOptions}
                allowClear
                showSearch
                mode="multiple"
                optionFilterProp="label"
                filterOption={customFilterOption}
                maxTagCount="responsive"
                placeholder={t('place-holder.all')}
              />
            </SearchItem>
            {/* 2. Phân nhóm chất thải (GPMT) */}
            <SearchItem label={t('masterdata.product.waste-group-waste')} name="wasteGroupingEnvironmentPermitIds">
              <Select
                options={wasteGroupOptions}
                allowClear
                showSearch
                mode="multiple"
                optionFilterProp="label"
                filterOption={customFilterOption}
                maxTagCount="responsive"
                placeholder={t('place-holder.all')}
              />
            </SearchItem>
            {/* 3. Phân nhóm chất thải (HĐ) */}
            <SearchItem label={t('masterdata.waste-product.waste-subgroup-contract')} name="wasteGroupingContractIds">
              <Select
                options={wasteGroupingContractOptions}
                allowClear
                showSearch
                mode="multiple"
                optionFilterProp="label"
                filterOption={customFilterOption}
                maxTagCount="responsive"
                placeholder={t('place-holder.all')}
              />
            </SearchItem>
            <SearchItem></SearchItem>
            {/* 4. Mã chất thải/ phế liệu */}
            <div className="relative">
              <SearchItem
                label={t('masterdata.waste-product.product-code')}
                name={'productCode'}
              >
                <Input placeholder={t('masterdata.waste-product.product-code')} />
              </SearchItem>
              <div
                onClick={() => {
                  setShowModalProductCode(true)
                }}
                className="absolute top-1/2 -right-6 cursor-pointer hover:text-blue-500">
                <HiOutlineExternalLink className="text-xl" />
              </div>
            </div>
            {/* 5. Tên chất thải/ phế liệu */}
            <SearchItem label={t('masterdata.waste-product.product-name')} name={'productName'}>
              <Input placeholder={t('masterdata.waste-product.product-name')} />
            </SearchItem>
            {/* 6. Trạng thái */}
            <SearchItem label={t('masterdata.product.status')} name={'actived'} initialValue={true}>
              <Select
                options={dropdownStatus}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={customFilterOption}
                placeholder={t('place-holder.all')}
              />
            </SearchItem>


            <SearchItem label={'Bắt buộc xác nhận kế hoạch bàn giao'}
              name={'isAutoConfirmPlan'}
              initialValue={null}>
              <Select
                options={[
                  {
                    label: 'Tất cả',
                    value: null,
                  },
                  {
                    label: 'Có',
                    value: true,
                  },
                  {
                    label: 'Không',
                    value: false,
                  },
                ]}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={customFilterOption}
                placeholder={t('place-holder.all')}
              />
            </SearchItem>


          </SearchForm>
          <SearchMultiplePopup
            title="Mã chất thải/ phế liệu"
            open={showModalProductCode}
            onOpenChange={setShowModalProductCode}
            onOk={(value) => {
              form.setFieldValue('productCode', value)
            }}
            onReset={() => {
              form.setFieldValue('productCode', undefined)
            }}
          />
        </SearchBox>
      </PageWrapper>
    )
  })
}

export default createWasteProduct

